import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormGenerator, FormInput } from '@microservices/wiskey-react-components'
import { Check } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { Grid } from '@mui/material'

import { FormEventType } from '@pages/WindowsCreateOrEdit/types'

import { ScriptValueDialog } from '@components/ScriptValueDialog'

import {
  FIELD_BIND_TYPE_ENUM,
  FIELD_BIND_VALUE_TYPE_OPTIONS,
  GENERATOR_INPUT_TYPE,
  MODAL_TYPE,
} from '@constants'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { useEventEdit } from './hooks/useEventEdit'

type EventEditProps = {
  sectionIndex: number
  tabIndex: number
  initialValues: FormEventType
  onSave: (values: FormEventType) => void
  onCancel: () => void
}

export const EventEdit = ({
  sectionIndex,
  tabIndex,
  initialValues,
  onSave,
  onCancel,
}: EventEditProps) => {
  const { t } = useTranslation()

  const { state, handlers, data } = useEventEdit({
    sectionIndex,
    tabIndex,
    initialValues,
    onSave,
    onCancel,
  })

  const {
    eventOptions,
    windowVariablesOptions,
    inputValue,
    watchEditValueType,
    watchEditValue,
    watchObjectTab,
  } = data

  const { editEventForm, showScriptValueDialogTitle } = state
  const {
    handleCloseScriptDialog,
    handleSaveScriptDialog,
    handleChangeEventType,
    handleConfirm,
    handleCancel,
  } = handlers

  return (
    <FormProvider {...editEventForm}>
      {showScriptValueDialogTitle && (
        <ScriptValueDialog
          windowBoundaries
          isShow={showScriptValueDialogTitle}
          modalType={MODAL_TYPE.EDIT}
          objectFields={[]}
          title={t(`tooltips.title.editJs`)}
          value={watchEditValue as string}
          onClose={handleCloseScriptDialog}
          onSave={handleSaveScriptDialog}
        />
      )}
      <Grid
        container
        alignItems={'center'}
        display='flex'
        justifyContent='space-between'
        mt={'5px'}
        xs={12}
      >
        <Grid item xs={2.5}>
          <FormInput
            disableClearable
            autocompleteOptions={eventOptions}
            formInputContainerSx={{ mb: '0px' }}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={`${t('window.event.label')}`}
            labelPlacement={'top'}
            name={`event`}
            sx={{ width: 250 }}
            rules={{
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={2.5}>
          <FormInput
            disableClearable
            autocompleteOptions={windowVariablesOptions}
            formInputContainerSx={{ mb: '0px' }}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={`${t('window.event.postVariable.label')}`}
            labelPlacement={'top'}
            name={`postVariable`}
            sx={{ width: 250 }}
          />
        </Grid>
        <Grid item xs={2.5}>
          <FormInput
            disableClearable
            autocompleteOptions={FIELD_BIND_VALUE_TYPE_OPTIONS}
            formInputContainerSx={{ mb: '0px' }}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={`${t('window.event.valueType.label')}`}
            labelPlacement={'top'}
            name={`valueType`}
            sx={{ width: 250 }}
            rules={{
              required: true,
            }}
            onChangeAutocomplete={handleChangeEventType}
          />
        </Grid>
        <Grid item xs={2.5}>
          {watchEditValueType?.id !== FIELD_BIND_TYPE_ENUM.FIELD && (
            <FormGenerator inputs={inputValue} />
          )}
          {watchEditValueType?.id === FIELD_BIND_TYPE_ENUM.FIELD && (
            <Grid item>
              <ModalRefPathPicker
                hasClearInInput
                isRequired
                embeddedObjectPickerControllerName={`pathArray`}
                formInputContainerSx={{ mb: '0px' }}
                label={t('window.event.value.label')}
                labelPlacement='top'
                name={'value'}
                object={watchObjectTab}
                placeholder={t('placeholder.displayField')}
                sx={{
                  '& .MuiBox-root': {
                    mb: 0,
                  },
                  '& .MuiButton-root': {
                    marginX: 0,
                    mt: 3,
                    width: 64,
                  },
                }}
                sxPickButton={{
                  marginTop: '32px !important',
                }}
              />
            </Grid>
          )}
        </Grid>
        <Grid item>
          <Grid position={'relative'} top={'15px'}>
            <Button onClick={handleConfirm}>
              <Check color={'action'} fontSize='medium' />
            </Button>
            <Button onClick={handleCancel}>
              <CloseIcon color={'error'} fontSize='medium' />
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
